import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import FieldsSidebar from './components/FieldsSidebar';
import PdfViewer from './components/PdfViewer';

import './styles.scss';

import { setSelectedFile, setTemplateName } from '@/redux/CustomContracts/actions';
import {
  createContractTemplate,
  getAllFieldsForContract,
  getContractTemplate,
  updateCustomContract,
} from '@/services/contracts.service';
import { SETTINGS_MAP } from '@/utils/constants/settings';
import { NotificationManager } from 'react-notifications';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CalendarWithoutDates from '@/components/Icons/icons/CalendarWithoutDates';
import BriefcaseIcon from '@/components/Icons/icons/CustomContracts/BriefcaseIcon';
import BuildingIcon from '@/components/Icons/icons/CustomContracts/BuildingIcon';
import EmailIcon from '@/components/Icons/icons/CustomContracts/EmailIcon';
import FourHorizontalLines from '@/components/Icons/icons/CustomContracts/FourHorizontalLines';
import SignatureIcon from '@/components/Icons/icons/CustomContracts/SignatureIcon';
import UserIcon from '@/components/Icons/icons/CustomContracts/UserIcon';

import Topbar from './components/Topbar';

const getIconByKey = (iconKey) => {
  const iconMap = {
    user: <UserIcon />,
    email: <EmailIcon />,
    calendar: <CalendarWithoutDates color="var(--icon-default)" />,
    horizontalLines: <FourHorizontalLines />,
    building: <BuildingIcon />,
    briefcase: <BriefcaseIcon />,
    pencil: <SignatureIcon />,
  };
  return iconMap[iconKey] || <UserIcon />;
};

const PDFEditor = () => {
  const { templateId } = useParams();
  const { selectedFile, templateName } = useSelector((state) => state.customContract);
  const [fields, setFields] = useState([]);
  const [allFieldsData, setAllFieldsData] = useState([]);
  const pageInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sidebarFields, setSidebarFields] = useState({ dynamic: [], signature: [] });
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [fontFamily, setFontFamily] = useState('timesNewRoman');
  const [fontSize, setFontSize] = useState('14px');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchAllFields = async () => {
    setIsLoadingFields(true);
    try {
      const response = await getAllFieldsForContract();
      if (response?.data) {
        const transformedFields = response.data
          .map((field) => ({
            id: `template-${field._id}`,
            label: field.label,
            type: field.type,
            icon: getIconByKey(field.icon),
            iconKey: field.icon,
            value: field.value,
            isRequired: field.isRequired,
            position: field.position || Infinity,
          }))
          .sort((a, b) => a.position - b.position);

        const categorizedFields = transformedFields.reduce(
          (acc, field) => {
            if (field.type?.includes('signature')) {
              acc.signature.push(field);
            } else {
              acc.dynamic.push(field);
            }
            return acc;
          },
          { dynamic: [], signature: [] }
        );

        setSidebarFields(categorizedFields);
        setAllFieldsData(transformedFields);

        dispatch({
          type: 'SET_REQUIRED_FIELDS',
          payload: transformedFields.filter((field) => field.isRequired).map((field) => field.id),
        });
      }
    } catch (error) {
      console.error('Error fetching all fields:', error);
      NotificationManager.error('Failed to load fields', '', 1200);
    } finally {
      setIsLoadingFields(false);
    }
  };

  useEffect(() => {
    fetchAllFields();
  }, []);

  const navigateToContractTemplatesCard = () => {
    navigate(`/dashboard/settings?tab=general#${SETTINGS_MAP.CUSTOM_CONTRACT_TEMPLATES.id}`);
  };

  useEffect(() => {
    if (templateId) {
      setIsEditMode(true);
      fetchTemplateData(templateId);
    } else if (!selectedFile) {
      navigateToContractTemplatesCard();
    }
  }, [templateId]);

  const fetchTemplateData = async (id) => {
    try {
      const response = await getContractTemplate(id);
      if (response.status === true) {
        const template = response.data;

        dispatch(setSelectedFile(template.pdfUrl));
        dispatch(setTemplateName(template.name));

        const transformedFields = template.fields.map((field) => {
          const fieldData = allFieldsData.find((f) => f.id === `template-${field.field}`);

          return {
            id: crypto.randomUUID(),
            templateId: `template-${field.field}`,
            x: field.x,
            y: field.y,
            page: parseInt(field.page),
            width: field.width,
            height: field.height,
            label: fieldData?.label || '',
            type: fieldData?.type || '',
            icon: fieldData?.icon,
            iconKey: fieldData?.iconKey,
            isRequired: fieldData?.isRequired || false,
          };
        });

        setFields(transformedFields);
        setFontFamily(template.fontFamily || 'timesNewRoman');
        setFontSize(template.fontSize || '14px');
      } else {
        NotificationManager.error('Failed to load template data', '', 1200);
        navigateToContractTemplatesCard();
      }
    } catch (error) {
      console.error('Error loading template:', error);
      NotificationManager.error('Failed to load template data', '', 1200);
      navigateToContractTemplatesCard();
    }
  };

  useEffect(() => {
    if (isEditMode && templateId && allFieldsData.length > 0) {
      fetchTemplateData(templateId);
    }
  }, [allFieldsData]);

  const handleFieldDrop = (fieldData, position, pageNumber) => {
    setFields((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        ...fieldData,
        x: position.x,
        y: position.y,
        page: pageNumber,
        width: 160,
        height: 32,
      },
    ]);
  };

  const handleFieldDragEnd = (fieldId, newPosition) => {
    setFields((prev) =>
      prev.map((field) => (field.id === fieldId ? { ...field, x: newPosition.x, y: newPosition.y } : field))
    );
  };
  const handleFieldResize = (fieldId, newWidth, newHeight, newX, newY) => {
    setFields((prevFields) =>
      prevFields.map((field) => {
        if (field.id === fieldId) {
          return {
            ...field,
            width: newWidth !== undefined ? newWidth : field.width,
            height: newHeight !== undefined ? newHeight : field.height,
            x: newX !== undefined ? newX : field.x,
            y: newY !== undefined ? newY : field.y,
          };
        }
        return field;
      })
    );
  };

  const handleFieldRemove = (fieldId) => {
    setFields((prev) => prev.filter((field) => field.id !== fieldId));
  };

  const getMissingRequiredFields = () => {
    const requiredFields = sidebarFields.signature
      .filter((field) => field.isRequired)
      .map((field) => ({
        id: field.id,
        label: field.label,
      }));

    const placedFieldIds = new Set(fields.map((field) => field.templateId));

    const missingFields = requiredFields.filter((field) => !placedFieldIds.has(field.id)).map((field) => field.label);

    return missingFields;
  };

  const handleSaveTemplate = async () => {
    const missingFields = getMissingRequiredFields();
    if (missingFields.length > 0) {
      NotificationManager.error(`Please add all required fields: ${missingFields.join(', ')}`, '', 1200);
      return;
    }

    setIsSubmitting(true);
    try {
      const formattedFields = fields.map((field) => ({
        field: field.templateId.replace('template-', ''),
        page: field.page.toString(),
        x: Math.round(field.x),
        y: Math.round(field.y),
        width: Math.round(field.width),
        height: Math.round(field.height),
      }));

      const templateData = {
        name: templateName,
        fields: formattedFields,
        fontFamily: fontFamily,
        fontSize: fontSize,
      };

      const response = isEditMode
        ? await updateCustomContract(templateId, templateData)
        : await createContractTemplate(selectedFile, templateData);

      if (response.status === true) {
        NotificationManager.success(`Template ${isEditMode ? 'updated' : 'created'} successfully`, '', 1200);
        dispatch(setSelectedFile(null));
        dispatch(setTemplateName(''));
        navigateToContractTemplatesCard();
      } else {
        NotificationManager.error(response.data || `Failed to ${isEditMode ? 'update' : 'create'} template`, '', 1200);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      NotificationManager.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} template`, '', 1200);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTextFieldValue = (fieldId, value) => {
    setFields((prevFields) => prevFields.map((field) => (field.id === fieldId ? { ...field, value } : field)));
  };
  const onFieldChange = (fieldId, updates) => {
    setFields((prevFields) => prevFields.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)));
  };

  const handleFontChange = (type, value) => {
    if (type === 'fontFamily') {
      setFontFamily(value);
    } else if (type === 'fontSize') {
      setFontSize(value);
    }
  };

  const handleBackClick = () => {
    if (selectedFile && templateName && !isEditMode) {
      const fileUrl = typeof selectedFile === 'string' ? selectedFile : URL.createObjectURL(selectedFile);

      sessionStorage.setItem(
        'pendingTemplate',
        JSON.stringify({
          fileUrl: fileUrl,
          name: templateName,
        })
      );
    }

    navigateToContractTemplatesCard();
    dispatch(setSelectedFile(null));
    dispatch(setTemplateName(''));
  };

  return (
    <div>
      <Topbar
        pageInputRef={pageInputRef}
        onSave={handleSaveTemplate}
        isSubmitting={isSubmitting}
        missingRequiredFields={getMissingRequiredFields()}
        isEditMode={isEditMode}
        onBackClick={handleBackClick}
      />
      <div className="test-pdf-container">
        <div className="pdf-editor">
          <div className="pdf-main">
            <PdfViewer
              file={selectedFile}
              fields={fields}
              onFieldDrop={handleFieldDrop}
              onFieldDragEnd={handleFieldDragEnd}
              onFieldResize={handleFieldResize}
              onFieldRemove={handleFieldRemove}
              pageInputRef={pageInputRef}
              updateTextFieldValue={updateTextFieldValue}
              onFieldChange={onFieldChange}
            />
          </div>
          <FieldsSidebar
            fields={sidebarFields}
            isLoading={isLoadingFields}
            fontFamily={fontFamily}
            fontSize={fontSize}
            onFontChange={handleFontChange}
          />
        </div>
      </div>
    </div>
  );
};

export default PDFEditor;
